import type { QuickInteractionCapture } from "../../../shared/src/domain/quick-interaction.js";
import type {
  LocationDetail,
  LabDetail,
  Lab,
  Location,
  Me,
  Organization,
  OrganizationLocation,
  Page,
  QueueItem,
  SavedInteraction,
  CorrectedObservation,
  ObservationCorrectionContext,
  SearchItem,
  ErrorDetails,
  WorkbenchQuery,
} from "./types.js";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: ErrorDetails | undefined;
  readonly requestId: string | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: ErrorDetails,
    requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }
}

export interface ApiClient {
  getMe(): Promise<Me>;
  getOrganizations(
    limit?: number,
    cursor?: string,
  ): Promise<Page<Organization>>;
  getOrganizationLocations(
    organizationId: string,
    limit?: number,
    cursor?: string,
  ): Promise<Page<OrganizationLocation>>;
  createOrganization(input: {
    name: string;
    organizationType?: string | null;
    createAnyway?: boolean;
  }): Promise<Organization>;
  createLocation(input: {
    organizationId: string;
    parentLocationId?: string | null;
    name: string;
    locationType: string;
  }): Promise<Location>;
  createLab(input: {
    primaryLocationId: string;
    name: string;
    department?: string;
    roomOrSuite?: string;
    notes?: string;
    createAnyway?: boolean;
  }): Promise<Lab>;
  search(query: string, contextLocationId?: string): Promise<Page<SearchItem>>;
  getWorkingQueue(query: WorkbenchQuery): Promise<{
    items: QueueItem[];
    nextCursor: string | null;
    calculatedForBusinessDate: string;
  }>;
  getLab(labId: string): Promise<LabDetail>;
  getLabInteractions(
    labId: string,
  ): Promise<Page<LabDetail["recentInteractions"]["items"][number]>>;
  getLocation(locationId: string): Promise<LocationDetail>;
  saveInteraction(capture: QuickInteractionCapture): Promise<SavedInteraction>;
  getObservationCorrectionContext(
    observationId: string,
  ): Promise<ObservationCorrectionContext>;
  correctObservation(
    observationId: string,
    clientRequestId: string,
    replacementPayload: Record<string, unknown>,
    optionalCorrectionNote?: string,
  ): Promise<CorrectedObservation>;
}

interface Envelope<T> {
  data: T;
}

interface ErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    details?: ErrorDetails;
    requestId?: string;
  };
}

export class HttpApiClient implements ApiClient {
  private readonly baseUrl: string;
  private readonly getAccessToken: () => Promise<string | null>;

  constructor(baseUrl: string, getAccessToken: () => Promise<string | null>) {
    this.baseUrl = baseUrl.replace(/\/$/, "");
    this.getAccessToken = getAccessToken;
  }

  getMe(): Promise<Me> {
    return this.request<Me>("/api/v1/me");
  }

  getOrganizations(limit = 50, cursor?: string): Promise<Page<Organization>> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) params.set("cursor", cursor);
    return this.request<Page<Organization>>(
      `/api/v1/organizations?${params.toString()}`,
    );
  }

  getOrganizationLocations(
    organizationId: string,
    limit = 50,
    cursor?: string,
  ): Promise<Page<OrganizationLocation>> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) params.set("cursor", cursor);
    return this.request<Page<OrganizationLocation>>(
      `/api/v1/organizations/${encodeURIComponent(organizationId)}/locations?${params.toString()}`,
    );
  }

  async createOrganization(input: {
    name: string;
    organizationType?: string | null;
    createAnyway?: boolean;
  }): Promise<Organization> {
    const result = await this.request<{ entity: Organization }>(
      "/api/v1/organizations",
      {
        method: "POST",
        body: JSON.stringify(input),
        headers: { "content-type": "application/json" },
      },
    );
    return result.entity;
  }

  async createLocation(input: {
    organizationId: string;
    parentLocationId?: string | null;
    name: string;
    locationType: string;
  }): Promise<Location> {
    const result = await this.request<{ entity: Location }>(
      "/api/v1/locations",
      {
        method: "POST",
        body: JSON.stringify(input),
        headers: { "content-type": "application/json" },
      },
    );
    return result.entity;
  }

  async createLab(input: {
    primaryLocationId: string;
    name: string;
    department?: string;
    roomOrSuite?: string;
    notes?: string;
    createAnyway?: boolean;
  }): Promise<Lab> {
    const result = await this.request<{ entity: Lab }>("/api/v1/labs", {
      method: "POST",
      body: JSON.stringify(input),
      headers: { "content-type": "application/json" },
    });
    return result.entity;
  }

  search(query: string, contextLocationId?: string): Promise<Page<SearchItem>> {
    const params = new URLSearchParams({ q: query, limit: "50" });
    if (contextLocationId) params.set("contextLocationId", contextLocationId);
    return this.request<Page<SearchItem>>(
      `/api/v1/search?${params.toString()}`,
    );
  }

  /**
   * One bounded, server-filtered page of Service Profile rows. The workbench
   * never fetches unbounded territory and never fetches Lab detail per row.
   */
  async getWorkingQueue(query: WorkbenchQuery): Promise<{
    items: QueueItem[];
    nextCursor: string | null;
    calculatedForBusinessDate: string;
  }> {
    const params = new URLSearchParams({ limit: String(query.limit ?? 50) });
    if (query.queueCode) params.set("queue", query.queueCode);
    if (query.organizationId)
      params.set("organizationId", query.organizationId);
    if (query.locationId) params.set("locationId", query.locationId);
    if (query.parentLocationId)
      params.set("parentLocationId", query.parentLocationId);
    if (query.serviceCategory)
      params.set("serviceCategory", query.serviceCategory);
    if (query.provider) params.set("provider", query.provider);
    if (query.contactPresence)
      params.set("contactPresence", query.contactPresence);
    if (query.includeChildLocations) {
      params.set("includeChildLocations", "true");
    }
    if (query.cursor) params.set("cursor", query.cursor);
    return this.request(`/api/v1/working-queues?${params.toString()}`);
  }

  getLab(labId: string): Promise<LabDetail> {
    return this.request<LabDetail>(`/api/v1/labs/${encodeURIComponent(labId)}`);
  }

  getLabInteractions(labId: string): Promise<LabDetail["recentInteractions"]> {
    return this.request<LabDetail["recentInteractions"]>(
      `/api/v1/labs/${encodeURIComponent(labId)}/interactions?limit=50`,
    );
  }

  getLocation(locationId: string): Promise<LocationDetail> {
    return this.request<LocationDetail>(
      `/api/v1/locations/${encodeURIComponent(locationId)}`,
    );
  }

  saveInteraction(capture: QuickInteractionCapture): Promise<SavedInteraction> {
    return this.request<SavedInteraction>("/api/v1/interactions", {
      method: "POST",
      body: JSON.stringify(capture),
      headers: { "content-type": "application/json" },
    });
  }

  getObservationCorrectionContext(
    observationId: string,
  ): Promise<ObservationCorrectionContext> {
    return this.request<ObservationCorrectionContext>(
      `/api/v1/observations/${encodeURIComponent(observationId)}/correction-context`,
    );
  }

  correctObservation(
    observationId: string,
    clientRequestId: string,
    replacementPayload: Record<string, unknown>,
    optionalCorrectionNote?: string,
  ): Promise<CorrectedObservation> {
    return this.request<CorrectedObservation>(
      `/api/v1/observations/${encodeURIComponent(observationId)}/correct`,
      {
        method: "POST",
        body: JSON.stringify({
          clientRequestId,
          replacementPayload,
          ...(optionalCorrectionNote === undefined
            ? {}
            : { optionalCorrectionNote }),
        }),
        headers: { "content-type": "application/json" },
      },
    );
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await this.getAccessToken();
    const headers = new Headers(init.headers);
    headers.set("accept", "application/json");
    if (token) headers.set("authorization", `Bearer ${token}`);

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${path}`, { ...init, headers });
    } catch {
      throw new ApiError(0, "api_unavailable", "The CRM API is unavailable.");
    }

    const payload = (await response.json().catch(() => null)) as
      | Envelope<T>
      | ErrorEnvelope
      | null;
    if (!response.ok) {
      const error = payload && "error" in payload ? payload.error : undefined;
      const data = payload && "data" in payload ? payload.data : undefined;
      if (
        response.status === 409 &&
        data !== null &&
        typeof data === "object" &&
        data !== undefined &&
        "status" in data &&
        data.status === "duplicate_review_required"
      ) {
        const candidates =
          "candidates" in data && Array.isArray(data.candidates)
            ? data.candidates
            : [];
        throw new ApiError(
          response.status,
          "duplicate_review_required",
          "Review possible matching records before creating this record.",
          { candidates },
        );
      }
      throw new ApiError(
        response.status,
        error?.code ?? "internal_error",
        error?.message ?? "The CRM request could not be completed.",
        error?.details,
        error?.requestId,
      );
    }
    if (!payload || !("data" in payload)) {
      throw new ApiError(
        502,
        "invalid_response",
        "The CRM returned an invalid response.",
      );
    }
    return payload.data;
  }
}
